"use client";

import { useMemo } from "react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@/components/ui/dropdown-menu";
import { Icon } from "@/components/ui/icon";
import { useAuth } from "@/features/auth";
import { useOrgMemberships } from "@/hooks/use-org";
import { cn } from "@/lib/utils";

import { useSwitchOrganization } from "./use-switch-organization";

function OrgAvatar({
  src,
  name,
  className,
  iconSize = 12,
}: {
  src?: string | null;
  name: string;
  className?: string;
  iconSize?: number;
}) {
  return (
    <Avatar
      className={cn(
        "shrink-0 rounded-[6px] shadow-[inset_0_0_0_1px_var(--border-alpha)]",
        className,
      )}
    >
      <AvatarImage src={src ?? undefined} alt={name} />
      <AvatarFallback className="rounded-[6px] bg-contrast/10">
        <Icon
          name="IconBuildings"
          size={iconSize}
          className="text-heading opacity-40"
        />
      </AvatarFallback>
    </Avatar>
  );
}

/**
 * Organization picker for the account menu: a submenu whose trigger shows the
 * current organization and whose panel lists every membership. Selecting a
 * row switches context via the shared switch flow.
 */
export function OrganizationSubmenu({
  onCreateOrganization,
}: {
  onCreateOrganization: () => void;
}) {
  const { organization } = useAuth();
  const switchOrg = useSwitchOrganization();
  // The dropdown is only mounted while open, so fetching here is lazy enough.
  const { data, isLoading } = useOrgMemberships();

  const memberships = useMemo(() => {
    const all = data?.memberships ?? [];
    return [...all].sort((a, b) => {
      if (a.is_active_context && !b.is_active_context) return -1;
      if (!a.is_active_context && b.is_active_context) return 1;
      return a.org_name.localeCompare(b.org_name);
    });
  }, [data]);

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger
        aria-label={
          organization
            ? `Organization: ${organization.name}. Switch organization`
            : "Select organization"
        }
        className="h-auto py-1.5 text-heading"
      >
        <OrgAvatar
          src={organization?.logo_url}
          name={organization?.name ?? ""}
          className="size-6"
        />
        <span className="flex min-w-0 flex-1 flex-col text-left">
          <span className="truncate text-sm leading-5 text-heading">
            {organization?.name ?? "Select organization"}
          </span>
          <span className="truncate text-[11px] leading-4 text-contrast/45">
            Organization
          </span>
        </span>
      </DropdownMenuSubTrigger>

      <DropdownMenuSubContent
        side="right"
        align="start"
        alignOffset={-4}
        className="w-[260px]"
      >
        {isLoading && !data ? (
          <div className="flex flex-col gap-1 p-1" aria-busy="true">
            {[0, 1].map((i) => (
              <div
                key={i}
                className="h-9 rounded bg-contrast/[0.04] shimmer shimmer-bg"
              />
            ))}
          </div>
        ) : memberships.length === 0 ? (
          <div className="px-2.5 py-3 text-xs text-contrast/45">
            You don&apos;t belong to any other organizations yet.
          </div>
        ) : (
          <div className="max-h-72 overflow-y-auto">
            {memberships.map((m) => (
              <DropdownMenuItem
                key={m.workos_org_id}
                disabled={m.is_active_context || switchOrg.isPending}
                aria-current={m.is_active_context ? "true" : undefined}
                className={cn(
                  "h-auto gap-2.5 py-1.5",
                  m.is_active_context && "data-[disabled]:opacity-100",
                )}
                onSelect={() => {
                  if (m.is_active_context || switchOrg.isPending) return;
                  switchOrg.mutate({ data: { workos_org_id: m.workos_org_id } });
                }}
              >
                <OrgAvatar
                  src={m.logo_url}
                  name={m.org_name}
                  className="size-7"
                  iconSize={14}
                />
                <span className="flex min-w-0 flex-1 flex-col text-left">
                  <span
                    className={cn(
                      "w-full truncate text-sm leading-5 text-heading",
                      m.is_active_context && "font-medium",
                    )}
                  >
                    {m.org_name}
                  </span>
                  <span className="w-full truncate text-[11px] leading-4 capitalize text-contrast/40">
                    {m.role}
                  </span>
                </span>
                {m.is_active_context && (
                  <Icon
                    name="IconSubscriptionTick1"
                    size={16}
                    className="shrink-0 text-ai"
                  />
                )}
              </DropdownMenuItem>
            ))}
          </div>
        )}

        <DropdownMenuSeparator />

        <DropdownMenuItem onSelect={onCreateOrganization}>
          <Icon
            name="IconPlusMedium"
            size={16}
            className="text-heading opacity-40"
          />
          Create organization
        </DropdownMenuItem>
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
}
